import React, { useState, useEffect } from 'react';
import './BoxShadows.css';
import { DEVICES } from '../../constants/breakpoints';

const BoxShadows = ({
  selectedElement,
  updateElementStyles,
  selectedDevice
}) => {
  const [shadows, setShadows] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Effect to sync shadows with selectedElement styles
  useEffect(() => {
    if (selectedElement?.styles) {
      let shadowString;
      
      // Get box-shadow based on device cascade
      if (selectedDevice === DEVICES.MOBILE) {
        shadowString = selectedElement.styles?.[DEVICES.MOBILE]?.boxShadow ||
                      selectedElement.styles?.[DEVICES.TABLET]?.boxShadow ||
                      selectedElement.styles?.[DEVICES.DESKTOP]?.boxShadow ||
                      selectedElement.styles?.boxShadow;
      } else if (selectedDevice === DEVICES.TABLET) {
        shadowString = selectedElement.styles?.[DEVICES.TABLET]?.boxShadow ||
                      selectedElement.styles?.[DEVICES.DESKTOP]?.boxShadow ||
                      selectedElement.styles?.boxShadow;
      } else {
        shadowString = selectedElement.styles?.[DEVICES.DESKTOP]?.boxShadow ||
                      selectedElement.styles?.boxShadow;
      }

      // Only parse and set shadows if we have a valid shadow string
      if (shadowString && shadowString !== 'none') {
        try {
          const parsedShadows = shadowString.split(',').map(shadow => {
            const parts = shadow.trim().split(' ');
            const isInset = parts[0] === 'inset';
            const startIndex = isInset ? 1 : 0;
            
            // Find the color part - it will be the last part that contains either # or rgba
            const colorParts = parts.slice(startIndex + 4);
            const color = colorParts.join(' ').match(/(#[0-9A-Fa-f]{6}|rgba?\([^)]+\))/)?.[0] || '#000000';
            
            return {
              offsetX: parseInt(parts[startIndex]) || 0,
              offsetY: parseInt(parts[startIndex + 1]) || 0,
              blur: parseInt(parts[startIndex + 2]) || 0,
              spread: parseInt(parts[startIndex + 3]) || 0,
              color: color,
              inset: isInset
            };
          });
          setShadows(parsedShadows);
        } catch (error) {
          console.error('Error parsing shadow string:', error);
          setShadows([]);
        }
      } else {
        setShadows([]);
      }
    } else {
      setShadows([]);
    }
  }, [selectedElement, selectedDevice]);

  const handleAddShadow = () => {
    const newShadow = {
      offsetX: 0,
      offsetY: 4,
      blur: 8,
      spread: 0,
      color: '#000000',
      inset: false
    };
    setShadows([...shadows, newShadow]);
    updateBoxShadow([...shadows, newShadow]);
    setIsExpanded(true);
  };

  const handleRemoveShadow = (index) => {
    const newShadows = shadows.filter((_, i) => i !== index);
    setShadows(newShadows);
    updateBoxShadow(newShadows);
  };

  const handleShadowChange = (index, property, value) => {
    const newShadows = shadows.map((shadow, i) => {
      if (i === index) {
        return { ...shadow, [property]: value };
      }
      return shadow;
    });
    setShadows(newShadows);
    updateBoxShadow(newShadows);
  };

  const updateBoxShadow = (shadows) => {
    const shadowString = shadows.length > 0 
      ? shadows.map(shadow => {
          const { offsetX, offsetY, blur, spread, color, inset } = shadow;
          return `${inset ? 'inset ' : ''}${offsetX}px ${offsetY}px ${blur}px ${spread}px ${color}`;
        }).join(', ')
      : 'none';

    // Update element styles for current device
    updateElementStyles(
      selectedElement.id,
      "boxShadow",
      shadowString,
      null,
      selectedDevice
    );
  };

  return (
    <div className="box-shadows-section">
      <div className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="section-title">
          <i className="fas fa-square-shadow"></i>
          <span>Box Shadow</span>
        </div>
        <div className="section-controls">
          <button className="add-shadow-btn" onClick={(e) => {
            e.stopPropagation();
            handleAddShadow();
          }}>
            <i className="fas fa-plus"></i>
          </button>
          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
        </div>
      </div>

      {isExpanded && shadows.map((shadow, index) => (
        <div key={index} className="shadow-item">
          <div className="shadow-header">
            <span>Shadow {index + 1}</span>
            <button 
              className="remove-shadow-btn"
              onClick={() => handleRemoveShadow(index)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="shadow-controls">
            <div className="control-columns">
              <div className="control-group">
                <label>X</label>
                <div className="slider-input-group">
                  <input
                    type="range"
                    min="-50"
                    max="50"
                    value={shadow.offsetX}
                    onChange={(e) => handleShadowChange(index, 'offsetX', parseInt(e.target.value))}
                  />
                  <input
                    type="number"
                    value={shadow.offsetX}
                    onChange={(e) => handleShadowChange(index, 'offsetX', parseInt(e.target.value) || 0)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>

              <div className="control-group">
                <label>Y</label>
                <div className="slider-input-group">
                  <input
                    type="range"
                    min="-50"
                    max="50"
                    value={shadow.offsetY}
                    onChange={(e) => handleShadowChange(index, 'offsetY', parseInt(e.target.value))}
                  />
                  <input
                    type="number"
                    value={shadow.offsetY}
                    onChange={(e) => handleShadowChange(index, 'offsetY', parseInt(e.target.value) || 0)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>

              <div className="control-group">
                <label>Blur</label>
                <div className="slider-input-group">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={shadow.blur}
                    onChange={(e) => handleShadowChange(index, 'blur', parseInt(e.target.value))}
                  />
                  <input
                    type="number"
                    value={shadow.blur}
                    onChange={(e) => handleShadowChange(index, 'blur', parseInt(e.target.value) || 0)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>

              <div className="control-group">
                <label>Spread</label>
                <div className="slider-input-group">
                  <input
                    type="range"
                    min="-50"
                    max="50"
                    value={shadow.spread}
                    onChange={(e) => handleShadowChange(index, 'spread', parseInt(e.target.value))}
                  />
                  <input
                    type="number"
                    value={shadow.spread}
                    onChange={(e) => handleShadowChange(index, 'spread', parseInt(e.target.value) || 0)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            </div>

            <div className="control-row color-row">
              <div className="control-group color-group">
                <label>Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={shadow.color.startsWith('#') ? shadow.color : '#000000'}
                    onChange={(e) => handleShadowChange(index, 'color', e.target.value)}
                  />
                  <input
                    type="text"
                    value={shadow.color}
                    onChange={(e) => {
                      const newColor = e.target.value;
                      if (newColor.match(/^#[0-9A-Fa-f]{0,6}$/) || 
                          newColor.match(/^rgba?\([^)]*\)$/)) {
                        handleShadowChange(index, 'color', newColor);
                      }
                    }}
                    placeholder="#000000"
                  />
                </div>
              </div>
              <div className="control-group inset-group">
                <label>Inset</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id={`inset-${index}`}
                    checked={shadow.inset}
                    onChange={(e) => handleShadowChange(index, 'inset', e.target.checked)}
                  />
                  <label htmlFor={`inset-${index}`}></label>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BoxShadows; 